import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE } from '../../lib/api';
import { mergeDeep, cloneDeep, formatMessage } from '../../utils/i18n';
import { STUDY_MATERIALS_COPY } from '../../i18n/materials';
import { confirmDialog } from '../../utils/dialogs';
import PreviewContent from './PreviewContent';

export default function StudyMaterialsModal({ onClose, copy: copyOverrides }) {
    const copy = useMemo(() => mergeDeep(cloneDeep(STUDY_MATERIALS_COPY.default), copyOverrides || {}), [copyOverrides]);
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [materialTitle, setMaterialTitle] = useState("");
    const [materialCategory, setMaterialCategory] = useState("general");
    const [materialDescription, setMaterialDescription] = useState("");
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [previewMaterial, setPreviewMaterial] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");

    useEffect(() => {
        fetchMaterials();
    }, []);

    const fetchMaterials = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE}/api/study-materials`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setMaterials(data);
            } else {
                throw new Error(copy.errors?.fetch || STUDY_MATERIALS_COPY.default.errors.fetch);
            }
        } catch (error) {
            console.error("Error fetching materials:", error);
            setError(copy.errors?.fetch || STUDY_MATERIALS_COPY.default.errors.fetch);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 50 * 1024 * 1024) { // 50MB limit
                setError(copy.errors?.invalidSize || STUDY_MATERIALS_COPY.default.errors.invalidSize);
                return;
            }
            setSelectedFile(file);
            setError("");
        }
    };

    const closePreview = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewMaterial(null);
        setPreviewUrl(null);
        setPreviewError("");
    };

    const handlePreview = async (material) => {
        try {
            setPreviewLoading(true);
            setPreviewError("");
            setPreviewMaterial(material);

            const token = localStorage.getItem('go2cdl_access_token');
            const response = await fetch(`${API_BASE}/api/study-materials/${material.id}/download${token ? `?token=${encodeURIComponent(token)}` : ''}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!response.ok) {
                throw new Error(copy.errors?.previewFailed || STUDY_MATERIALS_COPY.default.errors.previewFailed);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
        } catch (err) {
            console.error('Failed to preview material:', err);
            setPreviewError(copy.preview?.error || STUDY_MATERIALS_COPY.default.preview.error);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile || !materialTitle.trim()) {
            setError(copy.errors?.missingFields || STUDY_MATERIALS_COPY.default.errors.missingFields);
            return;
        }

        try {
            setUploading(true);
            setError("");

            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("title", materialTitle);
            formData.append("category", materialCategory);
            formData.append("description", materialDescription);

            const response = await fetch(`${API_BASE}/api/study-materials/upload`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
                body: formData,
            });

            if (response.ok) {
                const newMaterial = await response.json();
                setMaterials(prev => [newMaterial, ...prev]);
                setSelectedFile(null);
                setMaterialTitle("");
                setMaterialCategory("general");
                setMaterialDescription("");
                setShowUploadForm(false);
                await handlePreview(newMaterial);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || copy.errors?.uploadFailed || STUDY_MATERIALS_COPY.default.errors.uploadFailed);
            }
        } catch (error) {
            console.error("Error uploading material:", error);
            setError(error.message || copy.errors?.uploadFailed || STUDY_MATERIALS_COPY.default.errors.uploadFailed);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (materialId) => {
        if (!confirmDialog(copy.confirm?.delete || "Are you sure you want to delete this study material?")) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/api/study-materials/${materialId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
            });

            if (response.ok) {
                setMaterials(prev => prev.filter(m => m.id !== materialId));
                alert(copy.alerts?.deleteSuccess || STUDY_MATERIALS_COPY.default.alerts.deleteSuccess);
            } else {
                throw new Error(copy.errors?.deleteFailed || STUDY_MATERIALS_COPY.default.errors.deleteFailed);
            }
        } catch (error) {
            console.error("Error deleting material:", error);
            setError(copy.errors?.deleteFailed || STUDY_MATERIALS_COPY.default.errors.deleteFailed);
        }
    };

    const handleDownload = async (material) => {
        try {
            // Create a direct download link instead of using fetch
            const downloadUrl = `${API_BASE}/api/study-materials/${material.id}/download`;

            // Create a temporary link element
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = material.originalName || material.filename;
            a.target = '_blank';

            // Add authorization header by appending token to URL
            const token = localStorage.getItem('go2cdl_access_token');
            if (token) {
                a.href += `?token=${encodeURIComponent(token)}`;
            }

            // First check if the file exists by making a HEAD request
            const response = await fetch(downloadUrl + (token ? `?token=${encodeURIComponent(token)}` : ''), {
                method: 'HEAD'
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (errorData.error === 'File not found on server') {
                    alert(formatMessage(copy.alerts?.fileNotFound || STUDY_MATERIALS_COPY.default.alerts.fileNotFound, {
                        message: errorData.message,
                        title: errorData.title,
                        id: errorData.materialId,
                    }));
                    return;
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            // Trigger download
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading file:", error);
            alert(copy.alerts?.downloadFailed || STUDY_MATERIALS_COPY.default.alerts.downloadFailed);
        }
    };

    const getMaterialIcon = (mimeType) => {
        if (mimeType.includes('pdf')) return '📄';
        if (mimeType.includes('image')) return '🖼️';
        if (mimeType.includes('video')) return '🎥';
        if (mimeType.includes('audio')) return '🎵';
        return '📁';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-3 sm:p-4 z-50 pt-16 sm:pt-20">
                <div className="bg-white rounded-2xl w-full max-w-lg sm:max-w-6xl max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between p-5 sm:p-6 border-b">
                        <h2 className="text-2xl font-bold">{copy.header?.title || STUDY_MATERIALS_COPY.default.header.title}</h2>
                        <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                    </div>
                    <div className="p-5 sm:p-6">
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>
                        )}
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                            {copy.storageNotice || STUDY_MATERIALS_COPY.default.storageNotice}
                        </div>
                        <div className="mb-6">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                                <h3 className="text-lg font-semibold">{copy.upload?.heading || STUDY_MATERIALS_COPY.default.upload.heading}</h3>
                                <button onClick={() => setShowUploadForm(!showUploadForm)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                    {showUploadForm
                                        ? (copy.toggles?.hide || STUDY_MATERIALS_COPY.default.toggles.hide)
                                        : (copy.toggles?.show || STUDY_MATERIALS_COPY.default.toggles.show)}
                                </button>
                            </div>
                            {showUploadForm && (
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">{copy.upload?.labels?.title || STUDY_MATERIALS_COPY.default.upload.labels.title}</label>
                                            <input
                                                type="text"
                                                value={materialTitle}
                                                onChange={(e) => setMaterialTitle(e.target.value)}
                                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder={copy.upload?.placeholders?.title || STUDY_MATERIALS_COPY.default.upload.placeholders.title}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">{copy.upload?.labels?.category || STUDY_MATERIALS_COPY.default.upload.labels.category}</label>
                                            <select value={materialCategory} onChange={(e) => setMaterialCategory(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                                                <option value="general">{copy.upload?.options?.general || STUDY_MATERIALS_COPY.default.upload.options.general}</option>
                                                <option value="cdl-manual">{copy.upload?.options?.cdlManual || STUDY_MATERIALS_COPY.default.upload.options.cdlManual}</option>
                                                <option value="practice-tests">{copy.upload?.options?.practiceTests || STUDY_MATERIALS_COPY.default.upload.options.practiceTests}</option>
                                                <option value="training-videos">{copy.upload?.options?.trainingVideos || STUDY_MATERIALS_COPY.default.upload.options.trainingVideos}</option>
                                                <option value="safety-guidelines">{copy.upload?.options?.safetyGuidelines || STUDY_MATERIALS_COPY.default.upload.options.safetyGuidelines}</option>
                                                <option value="regulations">{copy.upload?.options?.regulations || STUDY_MATERIALS_COPY.default.upload.options.regulations}</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-slate-700 mb-2">{copy.upload?.labels?.description || STUDY_MATERIALS_COPY.default.upload.labels.description}</label>
                                        <textarea
                                            value={materialDescription}
                                            onChange={(e) => setMaterialDescription(e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            rows="3"
                                            placeholder={copy.upload?.placeholders?.description || STUDY_MATERIALS_COPY.default.upload.placeholders.description}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-slate-700 mb-2">{copy.upload?.labels?.file || STUDY_MATERIALS_COPY.default.upload.labels.file}</label>
                                        <input type="file" onChange={handleFileSelect} accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.mp3,.jpg,.jpeg,.png,.gif,.heic,.heif" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                        <p className="text-xs text-slate-500 mt-1">{copy.upload?.fileHint || STUDY_MATERIALS_COPY.default.upload.fileHint}</p>
                                    </div>
                                    <button onClick={handleUpload} disabled={!selectedFile || !materialTitle.trim() || uploading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                        {uploading ? (
                                            <>
                                                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                {copy.upload?.button?.uploading || STUDY_MATERIALS_COPY.default.upload.button.uploading}
                                            </>
                                        ) : (
                                            (copy.upload?.button?.upload || STUDY_MATERIALS_COPY.default.upload.button.upload)
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-4">{copy.list?.heading || STUDY_MATERIALS_COPY.default.list.heading}</h3>
                            {loading ? (
                                <div className="text-center py-8 text-slate-500">
                                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400 mr-2"></div>
                                    {copy.list?.loading || STUDY_MATERIALS_COPY.default.list.loading}
                                </div>
                            ) : materials.length === 0 ? (
                                <div className="text-center py-8 text-slate-500">
                                    <div className="text-4xl mb-4">📚</div>
                                    <p className="text-lg font-medium">{copy.list?.emptyTitle || STUDY_MATERIALS_COPY.default.list.emptyTitle}</p>
                                    <p className="text-sm">{copy.list?.emptyMessage || STUDY_MATERIALS_COPY.default.list.emptyMessage}</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {materials.map((material) => (
                                        <div
                                            key={material.id}
                                            className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                                        >
                                            <div className="flex items-start gap-3 sm:items-center">
                                                <div className="text-2xl">{getMaterialIcon(material.mimeType)}</div>
                                                <div>
                                                    <h4 className="font-medium text-slate-900">{material.title}</h4>
                                                    <div className="flex flex-wrap gap-2 text-sm text-slate-500">
                                                        <span className="capitalize">{material.category}</span>
                                                        <span>•</span>
                                                        <span>{formatFileSize(material.size)}</span>
                                                        <span>•</span>
                                                        <span>{new Date(material.uploadedAt).toLocaleDateString()}</span>
                                                    </div>
                                                    {material.description && (
                                                        <p className="text-sm text-slate-600 mt-1">{material.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 w-full sm:w-auto">
                                                <button onClick={() => handlePreview(material)} className="w-full sm:w-auto px-3 py-2 bg-slate-100 text-slate-700 rounded text-sm hover:bg-slate-200 transition-colors">{copy.list?.actions?.preview || STUDY_MATERIALS_COPY.default.list.actions.preview}</button>
                                                <button onClick={() => handleDownload(material)} className="w-full sm:w-auto px-3 py-2 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 transition-colors">{copy.list?.actions?.download || STUDY_MATERIALS_COPY.default.list.actions.download}</button>
                                                <button onClick={() => handleDelete(material.id)} className="w-full sm:w-auto px-3 py-2 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors">{copy.list?.actions?.delete || STUDY_MATERIALS_COPY.default.list.actions.delete}</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {previewMaterial && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80]">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
                        <button
                            onClick={closePreview}
                            className="absolute top-4 right-4 text-slate-500 hover:text-slate-700"
                            aria-label={copy.preview?.closeAria || STUDY_MATERIALS_COPY.default.preview.closeAria}
                        >
                            ✕
                        </button>
                        <div className="p-4 border-b">
                            <h3 className="text-lg font-semibold text-slate-900">{formatMessage(copy.preview?.title || STUDY_MATERIALS_COPY.default.preview.title, { title: previewMaterial.title })}</h3>
                            <p className="text-xs text-slate-500">{previewMaterial.originalName} · {previewMaterial.mimeType}</p>
                        </div>
                        <div className="p-4 h-[70vh] overflow-y-auto bg-slate-50">
                            {previewLoading && (
                                <div className="flex items-center justify-center h-full text-slate-500">{copy.preview?.loading || STUDY_MATERIALS_COPY.default.preview.loading}</div>
                            )}
                            {!previewLoading && previewError && (
                                <div className="text-red-600 text-sm text-center">{previewError}</div>
                            )}
                            {!previewLoading && !previewError && previewUrl && (
                                <PreviewContent url={previewUrl} mime={previewMaterial.mimeType} />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
